<!--suppress JSNonASCIINames -->
<template>
  <div>
    <!-- 기본정보 -->
    <field-select-module v-model="form.stdInfo" :board="this.board" :is-print="isPrint" :types="['field_name', 'department', 'com_name', 'rank', 'writer_name', 'date']" />

    <!-- 평가 정보 -->
    <div>
      <h5 class="my-4"><i class="dot"></i>{{ $t('평가 정보') }}</h5>
      <div class="writeGridContainer">
        <div>
          <div>{{ $t('변호사명') }}</div>
          <ml-input :readonly="board.readonly" :isPrint="isPrint" v-model="form.변호사명" />
        </div>
        <div>
          <div>{{ $t('평가일') }}</div>
          <p v-if="isPrint" class="print-input flex-1">{{ form.평가일 }}</p>
          <date-selector v-else :readonly="board.readonly" v-model="form.평가일" />
        </div>
      </div>
    </div>

    <!-- 피평가자 -->
    <div>
      <h5 class="my-4"><i class="dot"></i>{{ $t('피평가자') }}</h5>
      <div class="writeGridContainer">
        <div>
          <div>{{ $t('업체명') }}</div>
          <p v-if="isPrint" class="print-input flex-1">{{ form.업체명 }}</p>
          <company-selector v-else :readonly="board.readonly" v-model="form.업체명" class="w-full flex-1" @select="inputCompany" />
        </div>
        <div>
          <div>{{ $t('수급업무') }}</div>
          <ml-input :readonly="board.readonly" :isPrint="isPrint" v-model="form.수급업무" />
        </div>
        <div>
          <div>{{ $t('인원수') }}</div>
          <NumberInput :readonly="board.readonly" :isPrint="isPrint" v-model="form.인원수" />
        </div>
      </div>
    </div>

    <!-- 주요 검토 현황 -->
    <TableScorePercent :isPrint="isPrint" :readonly="board.readonly" title="주요 검토 현황" v-model="form.scoreDatas" @setTotal="setTotal" :end-column-count="calculateEndColumnCount">
      <template #cols>
        <col style="min-width: 164px"/>
        <col width="100" />
        <col width="100" />
        <col width="100" />
        <col width="100" />
        <col width="140" />
        <col width="200" />
      </template>
      <template #head>
        <tr>
          <th rowspan="2">{{ $t('평가지표') }}</th>
          <th colspan="2">{{ $t('법적 작성 기준') }}</th>
          <th colspan="2">{{ $t('기본 점수(가중치)') }}</th>
          <th rowspan="2">{{ $t('평가점수') }}</th>
          <th rowspan="2">{{ $t('변호사 의견') }}</th>
        </tr>
        <tr>
          <th>{{ $t('주기') }}</th>
          <th>{{ $t('횟수') }}</th>
          <th>{{ $t('기준(배점)') }}</th>
          <th>{{ $t('결과(소계)') }}</th>
        </tr>
      </template>
      <template #body="{ data }">
        <td :colspan="data.text1 ? 1 : 6" :style="{ color: data.titleColor }">
          <span v-if="data.type !== 'title' && !data.isNoBeforeIcon" class="mr-2">•</span>
          <span v-html="data.label"></span>
        </td>
        <td v-if="typeof data.text1 === 'string'" v-html="data.text1"></td>
        <td v-if="typeof data.text1 === 'string'" v-html="data.text2"></td>
        <td v-if="typeof data.text1 === 'string'" v-html="data.text3"></td>
        <td v-if="typeof data.text1 === 'string'" v-html="data.text4"></td>
      </template>
      <template #scoreSelector="{ data, updateScore }">
        <custom-option-selector
          v-if="data.scoreType === '점수'"
          :readonly="board.readonly"
          :options="[
            { value: 100, text: '우수' },
            { value: 75, text: '양호' },
            { value: 50, text: '보통' },
            { value: 25, text: '미흡' },
            { value: 0, text: '불량' },
          ]"
          placeholder="선택(점수)"
          v-model="data.select"
          @input="updateScore($event)"
          class="w-full"
          style="padding: 0 10px"
        />
        <custom-option-selector
          v-else-if="data.scoreType === '여부'"
          :readonly="board.readonly"
          :options="[
            { value: 100, text: '있음' },
            { value: 0, text: '없음' },
          ]"
          placeholder="선택(여부)"
          v-model="data.select"
          @input="updateScore($event)"
          class="w-full"
          style="padding: 0 10px"
        />
        <custom-option-selector
          v-else-if="data.scoreType === '여부 R'"
          :readonly="board.readonly"
          :options="[
            { value: 100, text: '없음' },
            { value: 0, text: '있음' },
          ]"
          placeholder="선택(여부 R)"
          v-model="data.select"
          @input="updateScore($event)"
          class="w-full"
          style="padding: 0 10px"
        />
        <custom-option-selector
          v-else-if="data.scoreType === '건수'"
          :readonly="board.readonly"
          :options="[
            { value: 0, text: '0건' },
            { value: 25, text: '1건 ~ 3건' },
            { value: 50, text: '4건 ~ 6건' },
            { value: 75, text: '7건 ~ 9건' },
            { value: 100, text: '10건 ~ 12건 이상' },
          ]"
          placeholder="선택(건수)"
          v-model="data.select"
          @input="updateScore($event)"
          class="w-full"
          style="padding: 0 10px"
        />
        <custom-option-selector
          v-else-if="data.scoreType === '건수 R'"
          :readonly="board.readonly"
          :options="[
            { value: 100, text: '0건' },
            { value: 75, text: '1건 ~ 3건' },
            { value: 50, text: '4건 ~ 6건' },
            { value: 25, text: '7건 ~ 9건' },
            { value: 0, text: '10건 이상' },
          ]"
          placeholder="선택(건수 R)"
          v-model="data.select"
          @input="updateScore($event)"
          class="w-full"
          style="padding: 0 10px"
        />
      </template>
      <template #endColumn="{ data }">
        <td v-for="i in calculateEndColumnCount">
          <ml-input v-if="data.endColumn && data.endColumn[i - 1] && data.endColumn[i - 1].type === 'input'"
                    :readonly="board.readonly"
                    v-model="data.endColumn[i - 1].content"
                    class="w-full"
                    style="padding: 0 10px"
          />
        </td>
      </template>
    </TableScorePercent>

    <!--    파일첨부-->
    <file-upload-module id="fileList" class="w-full mt-28px" v-model="form.fileList" :is-print="isPrint" :readonly="board.readonly"></file-upload-module>
  </div>
</template>

<script>
import CompanySelector from "@/components/selector/CompanySelector";
import TableScorePercent from "@/components/TableScorePercent.vue";
import CustomOptionSelector from "@/components/selector/CustomOptionSelectorObject";

import ScoreTableData from "./ScoreTableData";

export default {
  components: {
    CompanySelector,
    TableScorePercent,
    CustomOptionSelector,
  },
  props: {
    isPrint: Boolean,
    board: Object,
    value: Object,
  },
  data() {
    return {
      form: {
        stdInfo: {},

        변호사명: "",
        평가일: "",
        업체명: "",
        업체명_name: "",
        수급업무: "",
        인원수: "",

        scoreDatas: JSON.parse(JSON.stringify(ScoreTableData)),
        scoreTotal: null,
        scoreDatasSubColumCount: 1, // 기본 평가지표 하단에 추가 컬럼 개수

        photoList: [],
        fileList: [],
      },
    };
  },
  created() {
    this.initializeScoreDatas();
  },
  watch: {
    value: {
      immediate: true,
      deep: true,
      handler(value) {
        const keys = Object.keys(this.form);
        for (const key of keys) {
          if (value[key]) {
            this.form[key] = value[key];
          }
        }
      },
    },
    form: {
      immediate: true,
      deep: true,
      handler(value) {
        this.$emit("input", value);
      },
    },
  },
  computed: {
    calculateEndColumnCount() {
      // form.scoreDatas 내에서 endColumn 배열의 최대 길이를 계산
      let maxEndColumnCount = 0;

      if (this.form.scoreDatas) {
        this.form.scoreDatas.forEach(item => {
          if (item.endColumn && Array.isArray(item.endColumn)) {
            maxEndColumnCount = Math.max(maxEndColumnCount, item.endColumn.length);
          }
        });
      }

      return maxEndColumnCount;
    }
  },
  methods: {
    initializeScoreDatas() {
      this.form.scoreDatas = this.calculateText4Values(ScoreTableData);
    },
    calculateText4Values(data) {
      let accumulatedText4 = 0;
      let lastTitleIndex = null; // 마지막 "title"의 인덱스 추적

      return data.map((item, index) => {
        if (item.type === "title") {
          // 이전 "title"의 text4에 합산값 저장
          if (lastTitleIndex !== null) {
            data[lastTitleIndex].text4 = accumulatedText4.toString();
          }
          // 현재 "title"의 text4 초기화 및 인덱스 갱신
          lastTitleIndex = index;
          accumulatedText4 = 0;
          item.text4 = "0"; // 기본값 초기화
        } else if (item.text4) {
          // 숫자로 변환 후 합산
          accumulatedText4 += Number(item.text4) || 0;
        }
        return item;
      }).map((item, index) => {
        // 마지막 "title"에 누적합 저장
        if (index === lastTitleIndex) {
          item.text4 = accumulatedText4.toString();
        }
        return item;
      });
    },
    inputCompany(value) {
      this.form.업체명_name = value.com_name;
    },
    setTotal(value) {
      this.form.scoreTotal = value;

      this.calculateText4Values(this.form.scoreDatas);
    },
  },
};
</script>
